import React, { useState, useEffect } from 'react';
import { Expense, ExpenseStatus, UpdateExpenseData, EXPENSE_STATUS_LABELS, filterCategoriesByType } from '../types/expense';
import expensesApi from '../api/expenses';
import InfoTooltip from './InfoTooltip';
import { useAuth } from '../contexts/AuthContext';

interface ExpenseEditModalProps {
  expense: Expense;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedExpense: Expense) => void;
}

const ExpenseEditModal: React.FC<ExpenseEditModalProps> = ({
  expense,
  isOpen,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<UpdateExpenseData>({});
  const [categories, setCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentDateError, setPaymentDateError] = useState<string | null>(null);

  // Get document date (for confirmed expenses - from expense, for unconfirmed - from formData)
  const getDocumentDate = (): string => {
    return expense.isConfirmed ? expense.date : (formData.date || expense.date);
  };

  // Validate payment date: must not be earlier than document date (except for 'no_payment_required' status)
  const validatePaymentDate = (paymentDate: string | null | undefined, status: ExpenseStatus | undefined, documentDate: string): string | null => {
    if (status === 'no_payment_required') {
      return null;
    }

    if (status === 'paid' && !paymentDate) {
      return 'Укажите дату оплаты';
    }

    if (paymentDate && documentDate) {
      if (paymentDate < documentDate) {
        return 'Дата оплаты не может быть раньше даты документа';
      }
    }

    return null;
  };

  useEffect(() => {
    if (isOpen) {
      setFormData({
        date: expense.date,
        currency: expense.currency,
        name: expense.name,
        amount: Number(expense.amount),
        paymentDate: expense.paymentDate,
        status: expense.status,
        costCategory: expense.costCategory,
        contractor: expense.contractor,
        comment: expense.comment,
      });
      setError(null);
      setPaymentDateError(null);
    }
  }, [isOpen, expense]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await expensesApi.getCategories();
        setCategories(cats);
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    loadCategories();
  }, []);

  const handlePaymentDateChange = (newPaymentDate: string | null) => {
    const documentDate = getDocumentDate();
    const validationError = validatePaymentDate(newPaymentDate, formData.status, documentDate);
    setPaymentDateError(validationError);
    setFormData({ ...formData, paymentDate: newPaymentDate });
  };

  const handleStatusChange = (newStatus: ExpenseStatus) => {
    if (newStatus === 'paid') {
      // Show payment date field but do NOT auto-fill — user must choose explicitly
      setPaymentDateError(null);
      setFormData({ ...formData, status: newStatus, paymentDate: null });
    } else {
      // Clear paymentDate when status is not "paid"
      setPaymentDateError(null);
      setFormData({ ...formData, status: newStatus, paymentDate: null });
    }
  };

  const handleDocumentDateChange = (newDate: string) => {
    // When document date changes, also validate the payment date
    const validationError = validatePaymentDate(formData.paymentDate, formData.status, newDate);
    setPaymentDateError(validationError);
    setFormData({ ...formData, date: newDate });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validate payment date before submission (skip for income — no payment workflow)
    if (expense.type !== 'income') {
      const documentDate = getDocumentDate();
      const validationError = validatePaymentDate(formData.paymentDate, formData.status, documentDate);
      if (validationError) {
        setPaymentDateError(validationError);
        setIsLoading(false);
        return;
      }
    }

    try {
      let dataToSend: UpdateExpenseData;

      if (expense.isConfirmed && !isOperator) {
        dataToSend = {
          status: formData.status,
          paymentDate: formData.paymentDate,
        };
      } else if (isOperator) {
        // Operator can edit all fields except status
        const { status, ...rest } = formData;
        dataToSend = rest;
      } else {
        dataToSend = formData;
      }

      // Operator cannot change status — strip from payload
      if (isOperator) {
        const { status, paymentDate, ...rest } = dataToSend;
        dataToSend = rest;
      }

      const updated = await expensesApi.update(expense.id, dataToSend);
      onSave(updated);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка при сохранении');
    } finally {
      setIsLoading(false);
    }
  };

  const { isOperator, isTreasurer, isAdmin } = useAuth();

  if (!isOpen) return null;

  const allStatusOptions: ExpenseStatus[] = ['paid', 'unpaid', 'no_payment_required', 'pay_today', 'marked_for_deletion'];
  const treasurerStatuses: ExpenseStatus[] = ['paid', 'unpaid', 'no_payment_required', 'pay_today'];
  const treasurerDisabledStatuses: ExpenseStatus[] = ['pay_today'];
  const selectableStatuses: ExpenseStatus[] = isAdmin
    ? allStatusOptions
    : isTreasurer
      ? treasurerStatuses
      : []; // operator — no status control
  const isStatusOptionDisabled = (status: ExpenseStatus): boolean =>
    isTreasurer && !isAdmin && treasurerDisabledStatuses.includes(status);

  // Always include the current status so <select> displays it correctly,
  // even if the role cannot newly assign it (e.g. treasurer seeing pay_today)
  const statusOptions: ExpenseStatus[] =
    expense.status && !selectableStatuses.includes(expense.status)
      ? [expense.status, ...selectableStatuses]
      : selectableStatuses;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">
              {expense.isConfirmed
                ? (expense.type === 'income' ? 'Просмотр дохода' : 'Изменить статус')
                : (expense.type === 'income' ? 'Редактировать доход' : 'Редактировать расход')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Read-only summary for confirmed income — operator still edits base fields below */}
            {expense.isConfirmed && expense.type === 'income' && !isOperator && (
              <div className="space-y-2 text-sm text-gray-700 bg-green-50 p-4 rounded-md border border-green-200">
                <p><span className="font-medium">Название:</span> {expense.name}</p>
                <p><span className="font-medium">Сумма:</span> {expense.amount} {expense.currency}</p>
                <p><span className="font-medium">Дата:</span> {expense.date}</p>
                {expense.contractor && <p><span className="font-medium">Контрагент:</span> {expense.contractor}</p>}
                {expense.comment && <p><span className="font-medium">Комментарий:</span> {expense.comment}</p>}
                <p className="text-green-700 font-medium pt-1">Добавлено в Журнал операций</p>
              </div>
            )}

            {(!expense.isConfirmed || isOperator) && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Название
                  </label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Сумма
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount ?? ''}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value ? parseFloat(e.target.value) : undefined })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Валюта
                    </label>
                    <select
                      value={formData.currency || ''}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="BYN">BYN</option>
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                      <option value="PLN">PLN</option>
                      <option value="RUB">RUB</option>
                      <option value="UAH">UAH</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Дата
                  </label>
                  <input
                    type="date"
                    value={formData.date || ''}
                    onChange={(e) => handleDocumentDateChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {expense.type !== 'income' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Статья затрат
                    </label>
                    <select
                      value={formData.costCategory || ''}
                      onChange={(e) => setFormData({ ...formData, costCategory: e.target.value || null })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Не указана</option>
                      {filterCategoriesByType(categories, expense.type).map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Контрагент
                  </label>
                  <input
                    type="text"
                    value={formData.contractor || ''}
                    onChange={(e) => setFormData({ ...formData, contractor: e.target.value || null })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Комментарий
                  </label>
                  <textarea
                    value={formData.comment || ''}
                    onChange={(e) => setFormData({ ...formData, comment: e.target.value || null })}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </>
            )}

            {/* Status controls — hidden for operator and for income */}
            {!isOperator && expense.type !== 'income' && (
              <>
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                    Статус
                    <InfoTooltip text="При выборе статусов 'Не требует оплаты' и 'Оплачено' с выбором даты расход будет попадать в Журнал операций в вашей Google папке" />
                  </label>
                  <select
                    value={formData.status || ''}
                    onChange={(e) => handleStatusChange(e.target.value as ExpenseStatus)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status} disabled={isStatusOptionDisabled(status)}>
                        {EXPENSE_STATUS_LABELS[status]}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.status === 'paid' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Дата оплаты <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.paymentDate || ''}
                      onChange={(e) => handlePaymentDateChange(e.target.value || null)}
                      min={getDocumentDate()}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        paymentDateError ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''
                      }`}
                    />
                    {paymentDateError && (
                      <p className="mt-1 text-sm text-red-600">{paymentDateError}</p>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                {expense.isConfirmed && expense.type === 'income' ? 'Закрыть' : 'Отмена'}
              </button>
              {!(expense.isConfirmed && expense.type === 'income') && (
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? 'Сохранение...' : 'Сохранить'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ExpenseEditModal;
